/**
 * Faithful Wikipedia API - Structured Content Rendering
 *
 * Approach:
 * 1. Fetch intro from summary API (clean, no infobox)
 * 2. Fetch full HTML from html API (for infobox and sections)
 * 3. Extract infobox from section 0
 * 4. Extract remaining sections
 * 5. Render order: intro → infobox → sections
 */

export interface WikipediaFaithfulArticle {
  title: string;
  content: string;      // HTML content (structured on mobile, full on desktop)
  cssUrl: string;       // Wikipedia's CSS URL
  baseStyles: string;   // Inline critical styles
}

interface WikipediaSummary {
  extract_html: string; // Clean intro paragraph HTML
  title: string;
}

interface GetArticleOptions {
  mobileLayout?: boolean; // Use structured layout (intro → infobox → sections)
}

class WikipediaFaithfulAPIService {
  private cache = new Map<string, WikipediaFaithfulArticle>();

  async getArticle(title: string, options: GetArticleOptions = {}): Promise<WikipediaFaithfulArticle> {
    const { mobileLayout = false } = options;
    const cacheKey = `${title.toLowerCase()}-${mobileLayout ? 'mobile' : 'desktop'}`;

    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey)!;
    }

    try {
      console.log(`🔍 [FAITHFUL] Fetching content for: ${title} (${mobileLayout ? 'mobile' : 'desktop'})`);

      // Fetch full HTML (needed for both mobile and desktop)
      const htmlUrl = `https://en.wikipedia.org/api/rest_v1/page/html/${encodeURIComponent(title)}`;
      const htmlResponse = await fetch(htmlUrl);

      if (!htmlResponse.ok) {
        throw new Error(`Failed to fetch full HTML: ${htmlResponse.status}`);
      }

      const fullHtml = await htmlResponse.text();
      console.log(`✅ [FAITHFUL] Got full HTML (${fullHtml.length} chars)`);

      // Parse and extract base components
      const parser = new DOMParser();
      const doc = parser.parseFromString(fullHtml, 'text/html');

      // Extract CSS URL
      const cssUrl = this.extractCssUrl(doc);

      // Extract inline styles from body (not head!)
      const baseStyles = this.extractBaseStyles(doc);

      let content: string;

      if (mobileLayout) {
        // MOBILE: Structured layout (intro → infobox → sections)
        console.log(`📱 [FAITHFUL] Using mobile structured layout`);

        // Extract intro content (all paragraphs from section 0, excluding infobox)
        const introHtml = this.extractIntro(doc);

        // Extract infobox and remaining sections
        const infobox = this.extractInfobox(doc);
        const sections = this.extractSections(doc);

        // Combine in order
        content = this.combineContentMobile(introHtml, infobox, sections);
      } else {
        // DESKTOP: Use full Wikipedia HTML as-is
        console.log(`🖥️ [FAITHFUL] Using desktop full layout`);
        content = this.extractFullContent(doc);
      }

      const article: WikipediaFaithfulArticle = {
        title,
        content,
        cssUrl,
        baseStyles
      };

      this.cache.set(cacheKey, article);
      return article;

    } catch (error) {
      console.error(`❌ [FAITHFUL] Error fetching article "${title}":`, error);
      throw error;
    }
  }

  /**
   * Extract Wikipedia's CSS URL from document
   */
  private extractCssUrl(doc: Document): string {
    const cssLink = doc.querySelector('head link[rel="stylesheet"]');
    const cssHref = cssLink?.getAttribute('href');

    if (cssHref) {
      const url = cssHref.startsWith('//')
        ? `https:${cssHref}`
        : cssHref.startsWith('/')
        ? `https://en.wikipedia.org${cssHref}`
        : cssHref;
      console.log(`📎 [FAITHFUL] CSS URL found: ${url}`);
      return url;
    }

    // Fallback CSS
    const fallbackUrl = 'https://en.wikipedia.org/w/load.php?lang=en&modules=mediawiki.page.gallery.styles|ext.cite.parsoid.styles|ext.cite.styles|mediawiki.skinning.content.parsoid|mediawiki.skinning.interface|site.styles&only=styles&skin=vector';
    console.log(`⚠️ [FAITHFUL] CSS URL not found, using fallback`);
    return fallbackUrl;
  }

  /**
   * Extract inline styles from body (Wikipedia puts them there, not in head!)
   */
  private extractBaseStyles(doc: Document): string {
    // Wikipedia's REST API puts <style> tags in the body, not the head
    const bodyStyles = Array.from(doc.querySelectorAll('body style'))
      .map(style => style.textContent || '')
      .join('\n');

    // Also get any head styles
    const headStyles = Array.from(doc.querySelectorAll('head style'))
      .map(style => style.textContent || '')
      .join('\n');

    const combinedStyles = headStyles + '\n' + bodyStyles;
    console.log(`🎨 [FAITHFUL] Extracted ${combinedStyles.length} chars of inline styles`);
    return combinedStyles;
  }

  /**
   * Extract intro content from section 0 (all paragraphs and content, excluding infobox)
   */
  private extractIntro(doc: Document): string {
    const section0 = doc.querySelector('section[data-mw-section-id="0"]');
    if (!section0) {
      console.log(`⚠️ [FAITHFUL] Section 0 not found`);
      return '';
    }

    // Clone section 0 to avoid modifying the original
    const section0Clone = section0.cloneNode(true) as Element;

    // Remove infobox from the clone
    const infobox = section0Clone.querySelector('table.infobox');
    if (infobox) {
      infobox.remove();
    }

    // Get all remaining content (paragraphs, lists, etc.)
    const introHtml = section0Clone.innerHTML;
    console.log(`📝 [FAITHFUL] Extracted intro content (${introHtml.length} chars)`);
    return introHtml;
  }

  /**
   * Extract infobox from section 0
   */
  private extractInfobox(doc: Document): string | null {
    const section0 = doc.querySelector('section[data-mw-section-id="0"]');
    if (!section0) {
      console.log(`⚠️ [FAITHFUL] Section 0 not found`);
      return null;
    }

    // Find infobox table
    const infobox = section0.querySelector('table.infobox');
    if (!infobox) {
      console.log(`ℹ️ [FAITHFUL] No infobox found in section 0`);
      return null;
    }

    console.log(`📦 [FAITHFUL] Extracted infobox (${infobox.outerHTML.length} chars)`);
    return infobox.outerHTML;
  }

  /**
   * Extract all sections after section 0
   */
  private extractSections(doc: Document): string {
    // Get all sections except section 0
    const sections = Array.from(doc.querySelectorAll('section[data-mw-section-id]'))
      .filter(section => section.getAttribute('data-mw-section-id') !== '0');

    if (sections.length === 0) {
      console.log(`ℹ️ [FAITHFUL] No sections found after intro`);
      return '';
    }

    const sectionsHtml = sections.map(s => s.outerHTML).join('\n');
    console.log(`📑 [FAITHFUL] Extracted ${sections.length} sections (${sectionsHtml.length} chars)`);
    return sectionsHtml;
  }

  /**
   * Extract full content for desktop (original Wikipedia layout)
   */
  private extractFullContent(doc: Document): string {
    // Get the main content body
    const body = doc.querySelector('body');
    if (!body) {
      throw new Error('No body element found in Wikipedia HTML');
    }

    // Remove scripts and edit buttons only
    body.querySelectorAll('script').forEach(el => el.remove());
    body.querySelectorAll('.mw-editsection').forEach(el => el.remove());

    console.log(`🖥️ [FAITHFUL] Extracted full content (${body.innerHTML.length} chars)`);
    return body.innerHTML;
  }

  /**
   * Combine intro + infobox + sections for mobile (NO debug banner)
   */
  private combineContentMobile(intro: string, infobox: string | null, sections: string): string {
    // Combine in order: intro → infobox → sections
    const parts = [
      `<div class="intro-section">${intro}</div>`,
      infobox ? `<div class="infobox-section">${infobox}</div>` : '',
      sections ? `<div class="article-sections">${sections}</div>` : ''
    ];

    return parts.filter(Boolean).join('\n');
  }

  clearCache(): void {
    this.cache.clear();
  }
}

// Singleton instance
export const wikipediaFaithfulAPI = new WikipediaFaithfulAPIService();
